import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Select from 'react-select';
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";

import Spinner from '../../reusable/spinner/spinner.component';
import Client from '../../../tools/client.js';

import {
  ContentContainer,
  MainContainer,
  RowContainer,
  Subtitle,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  Title
} from '../../../styles/component.styles.jsx';

const client = new Client();

const ProductMetrics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [currentDateRange, setCurrentDateRange] = useState({});
  const [dateRangeType, setDateRangeType] = useState({ value: 'all', label: 'All' });
  const [dateRangePosition, setDateRangePosition] = useState(0);

  const options = [
    { value: 'all', label: 'All' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' }
  ];

  useEffect(() => {
    changeDateRange();
    // eslint-disable-next-line
  }, [dateRangeType, dateRangePosition]);

  const getAllData = async () => {
    setLoading(true);
    const res = await client.getTopProducts();
    setProducts(Array.isArray(res) ? res : []);
    setCurrentDateRange({});
    setLoading(false);
  };

  const changeDateRange = async () => {
    if (dateRangeType.value === 'all') {
      await getAllData();
      return;
    }

    setLoading(true);
    const now = dayjs();
    let adjusted = now;

    switch (dateRangeType.value) {
      case 'day':
        adjusted = now.subtract(dateRangePosition, 'day');
        break;
      case 'week':
        adjusted = now.subtract(dateRangePosition, 'week');
        break;
      case 'month':
        adjusted = now.subtract(dateRangePosition, 'month');
        break;
      case 'year':
        adjusted = now.subtract(dateRangePosition, 'year');
        break;
      default:
        break;
    }

    const start = adjusted.startOf(dateRangeType.value).format('YYYY-MM-DD');
    const end = adjusted.endOf(dateRangeType.value).format('YYYY-MM-DD');
    setCurrentDateRange({ start, end });

    const res = await client.getTopProducts(start, end);
    setProducts(Array.isArray(res) ? res : []);
    setLoading(false);
  };

  const goBack = () => {
    if (dateRangeType.value !== 'all') {
      setDateRangePosition(prev => prev + 1);
    }
  };

  const goForward = () => {
    if (dateRangeType.value !== 'all' && dateRangePosition > 0) {
      setDateRangePosition(prev => prev - 1);
    }
  };

  const changeDateRangeType = (newType) => {
    setDateRangePosition(0);
    setDateRangeType(newType);
  };

  return (
    <MainContainer>
      {loading ? (
        <Spinner />
      ) : (
        <ContentContainer>
          <Title>Top Products</Title>
          <RowContainer>
            <FaAngleLeft onClick={goBack} style={{ cursor: 'pointer' }} />
            <Select
              value={dateRangeType}
              onChange={changeDateRangeType}
              options={options}
            />
            <FaAngleRight onClick={goForward} style={{ cursor: 'pointer' }} />
          </RowContainer>
          {dateRangeType.value !== 'all' && (
            <Subtitle>
              {dateRangeType.value === 'day' && dayjs(currentDateRange.start).format('dddd MM/DD/YYYY')}
              {dateRangeType.value === 'week' && `${dayjs(currentDateRange.start).format('MM/DD/YYYY')} - ${dayjs(currentDateRange.end).format('MM/DD/YYYY')}`}
              {dateRangeType.value === 'month' && dayjs(currentDateRange.start).format('MMMM YYYY')}
              {dateRangeType.value === 'year' && dayjs(currentDateRange.start).format('YYYY')}
            </Subtitle>
          )}
          {products.length === 0 ? (
            <Subtitle>No product data for this period.</Subtitle>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Units Sold</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Orders</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow key={product.productId} cursor="pointer" onClick={() => navigate(`/products/${product.productId}`)}>
                    <TableData>{index + 1}</TableData>
                    <TableData>{product.name}</TableData>
                    <TableData>{parseInt(product.totalUnits)}</TableData>
                    <TableData>${(parseInt(product.totalRevenue) / 100).toFixed(2)}</TableData>
                    <TableData>{parseInt(product.orderCount)}</TableData>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ContentContainer>
      )}
    </MainContainer>
  );
};

export default ProductMetrics;
